import api from '../api';
import type { LotListItem, LotSearchParams } from './lots';

export type SearchAlertFilters = Omit<LotSearchParams, 'skip' | 'cursor' | 'limit'>;
export interface SearchAlertInput {
  name: string;
  filters: SearchAlertFilters;
  channels: { site: boolean; telegram: boolean };
}
export interface SearchAlert extends SearchAlertInput {
  id: string; paused: boolean; created_at: string; updated_at: string; unread_count: number; revision: number;
  access_blocked?: boolean; last_checked_at?: string | null; last_error?: string | null;
  pause_reason?: 'user_paused' | 'access_expired' | 'service_disabled' | 'quota_exceeded' | null;
}
export interface SearchAlertsResponse {
  items: SearchAlert[]; limit: number | null; active_count: number; unread_count: number;
  telegram_linked: boolean; enabled: boolean; check_interval_minutes: number;
  access_blocked?: boolean;
  read_only?: boolean;
  source_status?: { state: 'ok' | 'delayed' | 'unavailable'; last_success_at: string | null };
}
export interface SearchAlertMatch {
  id: string; lot_id: number; announcement_id?: number | null; announcement_number?: string | null;
  lot: LotListItem; matched_at: string; read: boolean;
}
export const listSearchAlerts = async (signal?: AbortSignal): Promise<SearchAlertsResponse> =>
  (await api.get('/search-alerts', { signal })).data;
export const getSearchAlert = async (id: string, signal?: AbortSignal): Promise<SearchAlert> =>
  (await api.get(`/search-alerts/${encodeURIComponent(id)}`, { signal })).data;
export const searchAlertUnread = async (signal?: AbortSignal): Promise<{ unread_count: number }> =>
  (await api.get('/search-alerts/summary', { signal })).data;
export const createSearchAlert = async (input: SearchAlertInput): Promise<SearchAlert> =>
  (await api.post('/search-alerts', input)).data;
export const updateSearchAlert = async (id: string, input: Partial<SearchAlertInput> & { paused?: boolean }): Promise<SearchAlert> =>
  (await api.patch(`/search-alerts/${encodeURIComponent(id)}`, input)).data;
export const deleteSearchAlert = async (id: string): Promise<void> => { await api.delete(`/search-alerts/${encodeURIComponent(id)}`); };
export const getSearchAlertMatches = async (id: string, skip: number, signal?: AbortSignal, limit = 20): Promise<{
  items: SearchAlertMatch[]; total: number; unread_count: number;
}> => (await api.get(`/search-alerts/${encodeURIComponent(id)}/matches`, { params: { skip, limit }, signal })).data;
export const markSearchAlertRead = async (id: string, matchIds?: string[]): Promise<void> => {
  await api.post(`/search-alerts/${encodeURIComponent(id)}/read`, matchIds ? { match_ids: matchIds } : {});
};
export function searchAlertErrorKey(error: any): string {
  const code = error?.response?.data?.detail?.code;
  if (code === 'search_alert_limit') return 'procurement.limit';
  if (code === 'telegram_not_linked') return 'procurement.telegramHint';
  if (code === 'search_alert_access_required') return 'procurement.denied';
  if (code === 'search_alert_storage_limit') return 'procurement.storageLimit';
  if (code === 'search_alert_concurrent_change') return 'procurement.concurrentChange';
  if (code === 'search_alert_journal_unavailable') return 'procurement.journalUnavailable';
  if (code === 'search_alert_not_found') return 'procurement.notFound';
  if (error?.response?.status === 422) return 'procurement.validationError';
  return 'procurement.error';
}

/** The display URL is not the persisted filter schema; explicitly map all inputs. */
export function alertFiltersToParams(filters: SearchAlertFilters, editId?: string): URLSearchParams {
  const params = new URLSearchParams();
  const keys: Record<string, string> = { anno_number: 'anno', customer_name: 'customer', trade_method: 'method',
    enstru_code: 'enstru', kpved_section: 'section', group: 'cat_group' };
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(keys[key] || key, Array.isArray(value) ? value.join(',') : String(value));
  });
  if (editId) params.set('edit_alert', editId);
  return params;
}
